import type { System } from '@/core/system';
import type { Time } from '@/core/time';
import { City, type CityState } from '@/world/city';
import { BehaviorMemory } from '@/ai/behavior-memory';
import { NarrativeController } from '@/ai/narrative-controller';
import type { PathRecorder } from '@/systems/path-recorder';

/**
 * Persistence. Palimpsest's entire point is that the world becomes a record of you,
 * so that record must survive a refresh — you return to the city you shaped, and it
 * keeps shaping. We snapshot the three things that constitute "the city's memory of
 * you": the raw behavioral memory, the narrative beats already witnessed, and the
 * full material state of the city (every building and bridge, its maturity and
 * vitality).
 *
 * Save is keyed by seed so a fresh seed starts a clean city. Autosave runs on a
 * gentle interval; load happens once at boot, before the world is generated.
 */
const KEY = 'echo-city-save';
// v2: city state gained ornaments + richer building compositions.
// v3: persists the walked-path recorder so the finale's ground-ink survives a reload.
const VERSION = 3;

interface SaveData {
  version: number;
  seed: number;
  savedAt: number;
  memory: object;
  narrative: string[];
  city: CityState;
  /** Flat [x0,z0,…] walked path, rounded to 0.1m. Optional: absent in older saves
   *  and skipped gracefully when no recorder is wired. */
  path?: number[];
}

export class SaveSystem implements System {
  readonly name = 'save';
  private timer = 0;
  private interval = 20;

  constructor(
    private seed: number,
    private city: City,
    private memory: BehaviorMemory,
    private narrative: NarrativeController,
    /** Optional so the composition root can wire the path layer independently. */
    private recorder?: PathRecorder,
  ) {}

  /** Attempt to load a save for this seed. Returns true if the city was restored. */
  load(): boolean {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(KEY);
      if (!raw) return false;
      const data = JSON.parse(raw) as SaveData;
      if (data.version !== VERSION || data.seed !== this.seed) return false;
      this.city.importState(data.city);
      this.memory.restore(data.memory as ReturnType<BehaviorMemory['serialize']>);
      this.narrative.restore(data.narrative);
      this.recorder?.restore(data.path);
      return true;
    } catch {
      // A save existed but could not be restored (corruption, transient dev-time
      // module weirdness). Park it in a backup slot so the autosave of the fresh
      // session that follows can never overwrite the player's only copy.
      try {
        if (raw) localStorage.setItem(KEY + '-rescued', raw);
      } catch {
        /* ignore */
      }
      return false;
    }
  }

  save(): void {
    try {
      const data: SaveData = {
        version: VERSION,
        seed: this.seed,
        savedAt: 0,
        memory: this.memory.serialize(),
        narrative: this.narrative.getFired(),
        city: this.city.exportState(),
        path: this.recorder?.serialize(),
      };
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch {
      /* storage full or unavailable — non-fatal */
    }
  }

  static clear(): void {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }

  update(time: Time): void {
    this.timer += time.delta;
    if (this.timer >= this.interval) {
      this.timer = 0;
      this.save();
    }
  }

  dispose(): void {
    this.save();
  }
}
